import chevronRight from "@assets/images/chevron-right.svg";
import { TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import { orderDetailUrl } from "@saleor/createOrder/urls";
import { ShoppingCartSortField } from "@saleor/createOrderExport/urls";
import { ExportCartsQuery } from "@saleor/graphql";
import useNavigator from "@saleor/hooks/useNavigator";
import { makeStyles } from "@saleor/macaw-ui";
import { renderCollection } from "@saleor/misc";
import { ListProps, RelayToFlat, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
import React from "react";
import { FormattedMessage } from "react-intl";

interface ICartTableProps extends ListProps, SortPage<ShoppingCartSortField> {
  data: RelayToFlat<ExportCartsQuery["exportCarts"]["carts"]>;
  settings: any;
  onUpdateListSettings: any;
  loading?: boolean;
  totalCount: number | null;
  latestPageItemNumber: number | null;
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
}

const useStyles = makeStyles(
  theme => ({
    tableWrapper: {
      border: `1px solid ${theme.palette.grey[300]}`,
      borderRadius: 6
    },
    centeredHeader: {
      "& > div": {
        justifyContent: "center"
      }
    },
    centeredCell: {
      textAlign: "center",
      color: theme.palette.grey[800],
      fontWeight: "bold",
      "& img": {
        cursor: "pointer"
      }
    },
    rowPointer: {
      cursor: "pointer"
    },
    tableHead: {
      "& .MuiTableCell-root, .MuiTableCell-root > div > div": {
        color: theme.palette.grey[600],
        fontWeight: "bold"
      }
    },
    colId: {
      color: theme.palette.grey[900]
    },
    colName: {
      color: theme.palette.grey[900]
    },
    colPi: {
      color: theme.palette.info.main,
      fontWeight: 700
    },
    noBold: {
      "& > div > div": {
        fontWeight: [400, "!important"] as any,
        color: theme.palette.grey[900]
      }
    },
    paginationWrapper: {
      marginTop: 16
    }
  }),
  { name: "CartTable" }
);

const CartTable: React.FC<ICartTableProps> = ({
  data,
  disabled,
  settings,
  totalCount,
  pageInfo,
  latestPageItemNumber,
  onUpdateListSettings,
  sort,
  onSort,
  onPreviousPage,
  onNextPage,
  onNextLastPage,
  onPreviousFirstPage
}) => {
  const classes = useStyles();
  const navigate = useNavigator();
  return (
    <>
      <ResponsiveTable wrapperClass={classes.tableWrapper}>
        <TableHead>
          <TableRow className={classes.tableHead}>
            <TableCellHeader>
              <FormattedMessage defaultMessage="PI No." />
            </TableCellHeader>
            <TableCellHeader
              direction={
                sort.sort === ShoppingCartSortField.solde_to
                  ? getArrowDirection(sort.asc)
                  : undefined
              }
              arrowPosition="right"
              data-testid="header-sold-to-button"
              onClick={() => onSort(ShoppingCartSortField.solde_to)}
            >
              <FormattedMessage defaultMessage="Sold to" />
            </TableCellHeader>
            <TableCellHeader className={classes.centeredHeader}>
              <FormattedMessage defaultMessage="จำนวนสินค้าในตะกร้า" />
            </TableCellHeader>
            <TableCellHeader
              className={classNames(classes.centeredHeader, classes.noBold)}
            >
              <FormattedMessage defaultMessage="ดูสินค้าในตะกร้า" />
            </TableCellHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {renderCollection(data, (item, index) => (
            <TableRow
              key={index}
              className={classes.rowPointer}
              onClick={() => navigate(orderDetailUrl(item.id))}
            >
              <TableCell className={classes.colPi}>
                {item ? item.pi.code : <Skeleton />}
              </TableCell>
              <TableCell className={classes.colName}>
                {item ? (
                  `${item.soldTo.code} - ${item.soldTo.name}`
                ) : (
                  <Skeleton />
                )}
              </TableCell>
              <TableCell className={classes.centeredCell}>
                {item ? item.totalItems : <Skeleton />}
              </TableCell>
              <TableCell className={classes.centeredCell}>
                <img src={chevronRight} alt="" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </ResponsiveTable>
      <div className={classes.paginationWrapper}>
        <TablePaginationCustom
          settings={settings}
          hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
          onNextPage={onNextPage}
          onPreviousFirstPage={onPreviousFirstPage}
          onNextLastPage={onNextLastPage}
          onUpdateListSettings={onUpdateListSettings}
          hasPreviousPage={
            pageInfo && !disabled ? pageInfo.hasPreviousPage : false
          }
          onPreviousPage={onPreviousPage}
          latestPageItemNumber={latestPageItemNumber}
          totalCount={totalCount}
          selectColor="#007E92"
          isBackFistPage
          isNextLastPage
          disabled={disabled}
        />
      </div>
    </>
  );
};

export default CartTable;
